/**
 * Slice 11: the Quick Play flow. Opening the site shows the roster screen (quickplay/screen.ts,
 * no login); Play starts a game with the team it built against the preset opponent, through the
 * same GameSession; after the final whistle Play again goes back to the roster, team kept. The
 * field (Phaser) is created on the first Play, never sized while hidden. `?seed=N` skips the
 * roster and plays the fixture teams, as before (screenshots, the e2e gate, debugging).
 *
 * Slice 8: the play screen. The player coaches Team A against the AI (Pro unless the Dev tab or
 * `?coach=` says otherwise). The screen walks the game a call at a time (docs/ui-style-guide.md
 * "The play screen (phone)"):
 *
 *   events since the last call ─► notes in the text box, snaps on the field (lines as they play,
 *   one motion with no holds; the result line lands at the whistle) ─► the player's call:
 *   Hurry · Book · Snap
 *   ─► the call goes into the session's decision list ─► the next events.
 *
 * Every outcome comes from packages/sim (GameSession replays seed + decisions); Phaser draws the
 * field, the HTML around it shows the sim's words and numbers.
 *
 * URL parameters (screenshots and debugging): `?seed=N`, `?coach=rookie|pro|allPro`,
 * `?opponent=electabuzz|raichu` (forces the preset the AI otherwise picks on Play), `?t=1.8`
 * freezes the first snap at that sim time, `?battles=1` pauses on every battle. The Dev tab's
 * "pause on key moments" brings back the 8a holds (crit, super effective, turnover, result).
 */
import '@fontsource/atkinson-hyperlegible/400.css';
import '@fontsource/pixelify-sans/400.css';
import '@fontsource/press-start-2p/400.css';
import './ui/tokens.css';
import './ui/app.css';
import './ui/buttons.css';
import './ui/field.css';
import './ui/textbox.css';
import './ui/sheet.css';
import './ui/log.css';
import './ui/board.css';
import './ui/book.css';
import './ui/roster.css';
import Phaser from 'phaser';
import {
  downName,
  sideSpotName,
  type Difficulty,
  type SnapDecision,
  type Team,
  type TeamId,
} from '@tdmons/sim';
import spriteGens from 'virtual:sprite-gens';
import { FieldScene, type FieldSceneData } from './field/FieldScene';
import { coachFromQuery, parseSeed, seedFromQuery, type PlayView } from './field/playView';
import { changeLines, theirChanges } from './game/aiChanges';
import { benchViews, boardRows } from './game/board';
import { preview, type Draft } from './game/draft';
import { rowAction, type RowAction, type RowPress } from './game/rowAction';
import { formationView } from './game/formation';
import {
  openingScoreboard,
  scoreboardAfter,
  scoreboardOf,
  scorebugText,
  type Scoreboard,
} from './game/scorebug';
import { DEFAULT_LEVEL, GameSession, PLAYER, type Prompt } from './game/session';
import { StepBuilder, teamTag, type Step } from './game/steps';
import { RosterScreen } from './quickplay/screen';
import { aiOpponent, opponentFromQuery, opponentTeam, type OpponentId } from './quickplay/state';
import { kittedBalancedTeams } from '@tdmons/sim/fixtures/balanced-teams';
import { holdTimes, pausing, snapLines, type Pauses } from './screen/reader';
import { Book } from './ui/book';
import { color, num } from './ui/tokens';
import { LogPanel } from './ui/logPanel';
import { renderBoard } from './ui/matchupBoard';
import { mountNamePlate } from './ui/namePlate';
import { SpeedPill, Toast, type Speed } from './ui/overlays';
import { ScorebugView } from './ui/scorebug';
import { Sheet, readPref, writePref } from './ui/sheet';
import { TextBox } from './ui/textBox';

/** Highest pixel ratio the canvas renders at; past 3 the GPU cost buys nothing visible. */
const MAX_DPR = 3;
const EVERY_BATTLE_KEY = 'tdmons.everyBattle';
const PAUSE_KEYS_KEY = 'tdmons.pauseKeys';

/** Device pixels per CSS pixel the canvas renders at. */
const canvasDpr = () => Math.min(MAX_DPR, Math.max(1, window.devicePixelRatio || 1));

function el<T extends HTMLElement>(id: string): T {
  const e = document.getElementById(id);
  if (!e) throw new Error(`index.html has no #${id}`);
  return e as T;
}

/** A fresh seed for a new game: the client picks it, the sim only ever receives it. */
function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] as number;
}

type Call = Extract<Prompt, { kind: 'call' }>;

/** "2nd & 3 at ELECTABUZZ 32": the spot read from the player's side of the field. */
function situation(p: Call, session: GameSession): string {
  const g = p.game;
  const ai = session.teams[PLAYER === 'A' ? 'B' : 'A'];
  if (p.conversion) return 'The try';
  const down = downName(g.down, g.toGo, g.ballOn);
  // The defense reads the spot from its own side of the field.
  const spot = p.side === 'offense' ? g.ballOn : 100 - g.ballOn;
  return `${down} at ${sideSpotName(spot, teamTag(ai))}`;
}

/** What the text box says while the player decides. */
function promptLine(p: Call, session: GameSession, draft: Draft): string {
  const ai = session.teams[PLAYER === 'A' ? 'B' : 'A'];
  if (p.conversion) {
    if (p.side === 'defense') return `${ai.name} go for two. Your call.`;
    return draft.conversion === 'two-point' ? 'Two-point try. Your call.' : 'The try. Your call.';
  }
  return p.side === 'offense'
    ? `${situation(p, session)}. Your call.`
    : `${ai.name} has it, ${situation(p, session)}. Call your defense.`;
}

async function boot(): Promise<void> {
  const params = new URLSearchParams(location.search);
  const stop = Number(params.get('t'));
  let stopAt = params.has('t') && Number.isFinite(stop) ? stop : null;
  const pauses: Pauses = {
    keyMoments: readPref(PAUSE_KEYS_KEY) === '1',
    everyBattle: params.get('battles') === '1' || readPref(EVERY_BATTLE_KEY) === '1',
  };

  // Phaser renders text to canvases once; the fonts must be ready first or it bakes a fallback.
  await Promise.all([
    document.fonts.load('11px "Pixelify Sans"'),
    document.fonts.load('9px "Press Start 2P"'),
    // Slice 11c: the text box's sentences; not on the canvas, but its first line should not swap.
    document.fonts.load('18px "Atkinson Hyperlegible"'),
  ]);

  const pill = new SpeedPill(el<HTMLButtonElement>('speed'), (s) => onSpeed(s));
  const box = new TextBox(el('textbox'), {
    cps: () => num('typewriter-cps'),
    readMs: () => num('t-read'),
    speed: () => (skippingSnap ? Infinity : pill.factor),
  });
  const toast = new Toast(el('toast'), () => num('t-toast'));
  const bug = new ScorebugView(el('scorebug'));
  const log = new LogPanel(el('panel-log'));
  new Sheet(el('sheet'));
  const namePlate = mountNamePlate(el('name-plate'));
  const actions = el('actions');
  const hurryButton = el<HTMLButtonElement>('btn-hurry');
  const book = new Book(el<HTMLDialogElement>('book'), {
    change: (d) => {
      draft = d;
      if (call) showPreSnap(call, d);
    },
    snap: (d) => {
      draft = d;
      press('snap');
    },
  });

  // The canvas renders in device pixels (one sprite pixel can be one screen pixel on a phone) and
  // is shown at CSS size through the scale manager's zoom; it follows #game's size.
  const host = el('game');
  let dpr = canvasDpr();
  const devicePx = (css: number) => Math.max(1, Math.round(css * dpr));
  const appEl = el('app');
  const rosterEl = el('roster');
  /** The field, created on the first game: a hidden #game would size it at 0. */
  let game: Phaser.Game | null = null;
  const mountGame = (): Phaser.Game => {
    if (game) return game;
    const g = new Phaser.Game({
      type: Phaser.AUTO,
      parent: host,
      backgroundColor: color('ground'),
      pixelArt: true,
      roundPixels: true,
      scale: {
        mode: Phaser.Scale.NONE,
        width: devicePx(host.clientWidth),
        height: devicePx(host.clientHeight),
        zoom: 1 / dpr,
      },
    });
    g.events.once(Phaser.Core.Events.READY, () => {
      const waiting = fieldWaiting;
      fieldWaiting = null;
      if (waiting) showField(waiting);
    });
    resize.observe(host);
    resize.observe(scorebugEl);
    game = g;
    return g;
  };
  /**
   * How much of the canvas's bottom the scorebug covers, in device px (slice 8c): the camera keeps
   * the ball above it. Measured on layout, not per frame.
   */
  const scorebugEl = el('scorebug');
  let safeInset = 0;
  const measureInset = () => {
    const field = host.getBoundingClientRect();
    const bugTop = scorebugEl.getBoundingClientRect().top;
    safeInset = scorebugEl.offsetHeight > 0 ? devicePx(Math.max(0, field.bottom - bugTop)) : 0;
  };
  const resize = new ResizeObserver(() => {
    if (!game) return;
    dpr = canvasDpr();
    if (game.scale.zoom !== 1 / dpr) game.scale.setZoom(1 / dpr);
    game.scale.resize(devicePx(host.clientWidth), devicePx(host.clientHeight));
    measureInset();
  });
  const scene = () => (game?.scene.getScene('field') as FieldScene | null | undefined) ?? null;
  /**
   * Put this snap or formation on the field. The scene is added once; Phaser only registers it at
   * boot, so a second call before then (the formation goes up during the opening notes) keeps the
   * latest data for boot instead of adding the scene twice.
   */
  let fieldAdded = false;
  let fieldWaiting: FieldSceneData | null = null;
  function showField(data: FieldSceneData): void {
    const s = scene();
    if (s) s.nextSnap(data);
    else if (!fieldAdded) {
      fieldAdded = true;
      mountGame().scene.add('field', FieldScene, true, data);
    } else fieldWaiting = data;
  }
  let session: GameSession;
  let steps: StepBuilder;
  let scoreboard: Scoreboard = openingScoreboard();
  /** Bumped on every new game, so a loop from an old game stops at its next step. */
  let run = 0;
  /** The snap on the field is being skipped: no holds but the result line. */
  let skippingSnap = false;
  /** Resolves the between-plays wait with the player's choice. */
  let choose: ((a: RowAction) => void) | null = null;
  /** The pending call and the player's draft for it (the Book edits it; Snap sends it). */
  let call: Call | null = null;
  let draft: Draft | null = null;

  const showBug = () => bug.show(scorebugText(scoreboard, session.teams));

  function onSpeed(s: Speed): void {
    if (s === 'skip') {
      skippingSnap = true;
      scene()?.skip();
      box.flush();
      return;
    }
    scene()?.setSpeed(s);
  }

  /**
   * A line between snaps, at reading speed. A score waits for a tap when pausing on key moments.
   * The final no longer does (slice 11): it stays in the box with Play again under it.
   */
  function playNote(step: Extract<Step, { kind: 'note' }>): Promise<void> {
    scoreboard = scoreboardAfter(scoreboard, step.event);
    showBug();
    log.beginSnap([], step.line);
    const hold = step.event.type === 'score' && pausing(pauses);
    return box.say({ text: step.line, hold });
  }

  /**
   * A snap on the field; resolves once the result line is done (read at reading speed, or tapped
   * past when pausing on key moments). With no pauses the result line lands at the whistle: lines
   * still queued then show whole at once (they are all in the log sheet, the recap).
   */
  function playSnap(view: PlayView, first: boolean): Promise<void> {
    skippingSnap = pill.speed === 'skip';
    toast.show(view.title);
    log.beginSnap(view.entries, view.situation);
    const lines = snapLines(view, pauses);
    const continuous = !pausing(pauses);
    return new Promise((resolve) => {
      const data: FieldSceneData = {
        view,
        stopAt: first ? stopAt : null,
        speed: pill.speed === 2 ? 2 : 1,
        holds: skippingSnap ? [] : holdTimes(lines),
        skip: skippingSnap,
        onEvent: (index) => {
          log.reveal(index);
          for (const l of lines) {
            if (l.entry.event !== index) continue;
            const line = skippingSnap && !l.whistle ? { ...l, hold: false } : l;
            const said = l.whistle && continuous ? box.sayNow(line) : box.say(line);
            void said.then(() => {
              if (l.whistle) resolve();
              else if (l.hold) scene()?.release(l.t);
            });
          }
        },
        onBanner: (text) => toast.show(text),
        namePlate,
        onWhistle: () => {},
        safeInset: () => safeInset,
      };
      showField(data);
    });
  }

  /**
   * The field between plays: both charts in formation, with the draft's personnel and play (the
   * sim places them). The matchup board in the sheet follows the same preview.
   */
  function showPreSnap(p: Call, d: Draft): void {
    const pv = preview(p.game, d);
    renderBoard(
      el('board'),
      boardRows(pv, PLAYER),
      undefined,
      benchViews(pv, PLAYER),
      theirChanges(p.aiChanges ?? []),
    );
    const data: FieldSceneData = {
      view: formationView(p.game, d),
      stopAt: null,
      speed: 1,
      holds: [],
      onEvent: () => {},
      onBanner: () => {},
      namePlate,
      onWhistle: () => {},
      safeInset: () => safeInset,
    };
    showField(data);
  }

  /** 4th down or the try, on offense: the call in the text box. 'sent' when it kicks. */
  async function special(p: Call, d: Draft): Promise<'sent' | Draft> {
    if (p.conversion) {
      const c = await box.choice('Touchdown! The try: kick it, or go for two?', [
        { label: 'Kick', value: 'kick' },
        { label: 'Go for two', value: 'two-point' },
      ]);
      if (c === 'kick') {
        // A kick has no play to pick; the decision still names one (the sim ignores it).
        session.snap({ ...d, playId: d.playId ?? session.playFor('offense'), conversion: 'kick' });
        return 'sent';
      }
      return { ...d, conversion: 'two-point' };
    }
    const c = await box.choice(`${situation(p, session)}. Go for it, punt, or kick?`, [
      { label: 'Go', value: 'go' },
      { label: 'Punt', value: 'punt' },
      { label: 'FG', value: 'field-goal' },
    ]);
    if (c === 'punt' || c === 'field-goal') {
      session.snap({ ...d, playId: d.playId ?? session.playFor('offense'), fourthDown: c });
      return 'sent';
    }
    return { ...d, fourthDown: 'go' };
  }

  /** Between plays: the prompt in the text box and Hurry · Book · Snap until one is pressed. */
  function between(p: Call, d: Draft): Promise<RowAction> {
    box.prompt(promptLine(p, session, d));
    actions.classList.remove('final');
    hurryButton.hidden = p.side !== 'offense';
    actions.classList.toggle('no-hurry', p.side !== 'offense');
    actions.hidden = false;
    return new Promise((resolve) => (choose = resolve));
  }

  async function loop(id: number): Promise<void> {
    let first = true;
    /** The AI's changes said before the last call: the snap's own lines for them are skipped. */
    let announced: string[] = [];
    for (;;) {
      const { events, prompt } = session.next();
      const walk = steps.push(events);
      // Nothing to animate before this call (the opening toss and kickoff, a punt): the next
      // formation goes up first, so the field is never blank.
      if (prompt.kind === 'call' && !walk.some((s) => s.kind === 'snap')) {
        showPreSnap(prompt, session.draftFor(prompt));
      }
      for (const step of walk) {
        if (id !== run) return;
        if (step.kind === 'note') {
          const said = step.event.type === 'sub' ? announced.indexOf(step.line) : -1;
          if (said >= 0) announced.splice(said, 1);
          else await playNote(step);
        } else {
          scoreboard = scoreboardAfter(scoreboard, step.event);
          showBug();
          await playSnap(step.view, first);
          first = false;
          stopAt = null; // `?t=` freezes the page's first snap only
        }
      }
      if (id !== run) return;
      if (prompt.kind === 'final') {
        // After the final whistle: Play again, back to the roster with the team kept.
        hurryButton.hidden = true;
        actions.classList.add('final');
        actions.hidden = false;
        return;
      }
      scoreboard = scoreboardOf(prompt.game);
      showBug();
      let d = session.draftFor(prompt);
      showPreSnap(prompt, d);
      if (prompt.scripted) {
        session.playScripted();
        continue;
      }
      if (prompt.side === 'offense' && (prompt.fourthDown || prompt.conversion)) {
        const settled = await special(prompt, d);
        if (id !== run) return;
        if (settled === 'sent') continue;
        d = settled;
      }
      announced = [];
      // Slice 11c: the AI offense's subs and moves, one plain line each, before the player calls.
      for (const line of changeLines(prompt.aiChanges ?? [], session.teams)) {
        log.beginSnap([], line);
        await box.say({ text: line, hold: false });
        if (id !== run) return;
        announced.push(line);
      }
      call = prompt;
      draft = d;
      const action = await between(prompt, d);
      if (id !== run) return;
      actions.hidden = true;
      book.close();
      call = null;
      draft = null;
      if (action.kind === 'hurry') session.hurry(action.draft);
      else if (action.kind === 'snap') session.snap(action.draft);
    }
  }

  /** The teams in play: the roster screen's, or the fixture teams on the `?seed=` path. */
  let teams: Record<TeamId, Team> | undefined;
  /** Started from a `?seed=` link: the URL follows the game, as before slice 11. */
  const fromLink = seedFromQuery(location.search) !== null;
  /** `?opponent=`: a forced preset instead of the AI's pick (slice 10c). */
  const forced = opponentFromQuery(location.search);
  /** The Dev tab's opponent: the AI's pick, or a forced preset. */
  let devOpponent: OpponentId | 'ai' = forced ?? (fromLink ? 'raichu' : 'ai');

  /** `t` with `id` (or the AI's pick) as team B, the player's name kept apart from its name. */
  function withOpponent(t: Record<TeamId, Team>, id: OpponentId | 'ai'): Record<TeamId, Team> {
    const B = opponentTeam(id === 'ai' ? aiOpponent(t.A) : id);
    const base = t.A.name.replace(/ \(You\)$/, '');
    return { A: { ...t.A, name: base === B.name ? `${base} (You)` : base }, B };
  }

  function newGame(seed: number, level: Difficulty, script: readonly SnapDecision[] = []): void {
    run += 1;
    showPlay();
    choose = null;
    call = null;
    draft = null;
    book.close();
    session = new GameSession(seed, level, teams, script);
    steps = new StepBuilder(session.teams, seed);
    scoreboard = openingScoreboard();
    showBug();
    log.reset();
    box.reset();
    toast.hide();
    actions.hidden = true;
    actions.classList.remove('final');
    el('dev-seed').textContent = String(seed);
    el<HTMLInputElement>('seed-input').value = '';
    for (const b of document.querySelectorAll<HTMLButtonElement>('[data-level]')) {
      b.setAttribute('aria-pressed', String(b.dataset.level === level));
    }
    for (const b of document.querySelectorAll<HTMLButtonElement>('[data-opponent]')) {
      b.setAttribute('aria-pressed', String(b.dataset.opponent === devOpponent));
    }
    if (fromLink) history.replaceState(null, '', `?seed=${seed}&coach=${level}`);
    if (import.meta.env.DEV) {
      // `game` is a getter: the field is created on the first snap shown, after this runs.
      const hook = {
        get game() {
          return game;
        },
        session,
        box,
        safeInset: () => safeInset,
      };
      Object.assign(window, { tdmons: hook });
    }
    void loop(run);
  }

  function showPlay(): void {
    roster.hide();
    appEl.hidden = false;
    game?.resume();
  }

  /** Back to the roster: the game in play stops, the field sleeps, the team is as it was. */
  function showRoster(): void {
    run += 1;
    choose = null;
    book.close();
    actions.hidden = true;
    appEl.hidden = true;
    game?.pause();
    roster.show();
  }

  const roster = new RosterScreen(
    rosterEl,
    spriteGens.gens,
    {
      play: (built, level) => {
        teams = built;
        devOpponent = forced ?? 'ai';
        newGame(randomSeed(), level);
      },
    },
    forced,
  );
  el('btn-again').addEventListener('click', showRoster);

  /** Open the Book on this call's draft, on `tab` (or where it was; Plays while no play). */
  function openBook(tab?: 'plays'): void {
    if (!call || !draft) return;
    book.open(
      {
        game: call.game,
        draft,
        situation: situation(call, session).toUpperCase(),
        canHurry: call.side === 'offense',
        theirs: theirChanges(call.aiChanges ?? []),
      },
      tab,
    );
  }

  /**
   * Snap or Hurry, from the action row or the Book (slice 11c): with no play to send, the Book
   * opens on Plays instead; otherwise the call goes, always with a play (`rowAction`).
   */
  function press(p: RowPress): void {
    if (!call || !draft || !choose) return;
    const a = rowAction(p, draft, session.hurryPlay());
    if (a.kind === 'book') openBook(a.tab);
    else choose(a);
  }

  el('btn-snap').addEventListener('click', () => press('snap'));
  el('btn-hurry').addEventListener('click', () => press('hurry'));
  el('btn-book').addEventListener('click', () => openBook());

  el('btn-replay').addEventListener('click', () =>
    newGame(session.seed, session.level, [...session.decisions]),
  );
  el('btn-new').addEventListener('click', () => newGame(randomSeed(), session.level));
  for (const b of document.querySelectorAll<HTMLButtonElement>('[data-level]')) {
    b.addEventListener('click', () =>
      newGame(session.seed, coachFromQuery(`?coach=${b.dataset.level ?? ''}`) ?? session.level),
    );
  }
  // Forcing a preset (slice 10c): the same game again against it, or against the AI's pick.
  for (const b of document.querySelectorAll<HTMLButtonElement>('[data-opponent]')) {
    b.addEventListener('click', () => {
      const v = b.dataset.opponent;
      devOpponent = v === 'electabuzz' || v === 'raichu' ? v : 'ai';
      teams = withOpponent(teams ?? kittedBalancedTeams(), devOpponent);
      newGame(session.seed, session.level);
    });
  }
  const seedInput = el<HTMLInputElement>('seed-input');
  el<HTMLFormElement>('dev-form').addEventListener('submit', (ev) => {
    ev.preventDefault();
    const typed = parseSeed(seedInput.value);
    el('seed-error').hidden = typed !== null;
    if (typed === null) {
      seedInput.setAttribute('aria-invalid', 'true');
      return;
    }
    seedInput.removeAttribute('aria-invalid');
    newGame(typed, session.level);
  });
  const keysBox = el<HTMLInputElement>('pause-keys');
  keysBox.checked = pauses.keyMoments;
  keysBox.addEventListener('change', () => {
    pauses.keyMoments = keysBox.checked;
    writePref(PAUSE_KEYS_KEY, pauses.keyMoments ? '1' : '0');
  });
  const everyBox = el<HTMLInputElement>('every-battle');
  everyBox.checked = pauses.everyBattle;
  everyBox.addEventListener('change', () => {
    pauses.everyBattle = everyBox.checked;
    writePref(EVERY_BATTLE_KEY, pauses.everyBattle ? '1' : '0');
  });

  if (fromLink) {
    if (forced) teams = withOpponent(kittedBalancedTeams(), forced);
    newGame(
      seedFromQuery(location.search) ?? randomSeed(),
      coachFromQuery(location.search) ?? DEFAULT_LEVEL,
    );
  } else {
    roster.show();
    await roster.start();
  }
  if (import.meta.env.DEV) Object.assign(window, { tdmonsRoster: roster });
}

void boot();
